import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScanLine, Keyboard } from "lucide-react";

export default function QRScanner({ onResult }) {
  const [manual, setManual] = useState("");
  const [cameraError, setCameraError] = useState(null);
  const [scanning, setScanning] = useState(false);
  const scannerRef = useRef(null);
  const startedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    const start = async () => {
      if (startedRef.current) return;
      startedRef.current = true;
      try {
        const scanner = new Html5Qrcode("qr-reader-region");
        scannerRef.current = scanner;
        await scanner.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 220, height: 220 } },
          (decoded) => {
            if (!cancelled) {
              onResult(decoded);
            }
          },
          () => {}
        );
        if (!cancelled) setScanning(true);
      } catch (e) {
        if (!cancelled) setCameraError("Camera unavailable or permission denied. Use manual entry below.");
      }
    };
    start();
    return () => {
      cancelled = true;
      const s = scannerRef.current;
      if (s) {
        try {
          const p = s.isScanning ? s.stop() : Promise.resolve();
          p.then(() => s.clear()).catch((e) => console.error("QR scanner cleanup failed:", e));
        } catch (e) {
          console.error("QR scanner stop failed:", e);
        }
      }
    };
  }, [onResult]);

  return (
    <div className="space-y-4" data-testid="qr-scanner">
      {!cameraError && (
        <div className="rounded-lg overflow-hidden border border-border bg-black">
          <div id="qr-reader-region" className="w-full" />
        </div>
      )}
      {cameraError && (
        <p className="text-sm text-signal-amber" data-testid="qr-camera-error">{cameraError}</p>
      )}
      {scanning && (
        <p className="text-xs text-muted-foreground font-mono uppercase tracking-widest flex items-center gap-2">
          <ScanLine className="w-4 h-4 text-signal-green animate-pulse" /> Point camera at QR / barcode
        </p>
      )}
      <div className="flex gap-2">
        <Input
          data-testid="scan-manual-input"
          placeholder="Or type code manually (e.g. INV-2026-00852)"
          value={manual}
          onChange={(e) => setManual(e.target.value)}
          className="font-mono"
        />
        <Button data-testid="scan-manual-submit" variant="secondary" onClick={() => manual.trim() && onResult(manual.trim())}>
          <Keyboard className="w-4 h-4 mr-1" /> Lookup
        </Button>
      </div>
    </div>
  );
}
